import React from 'react'
import { SafeAreaBottom } from '@/components/ui/SafeAreaBottom'

interface CallControlDockProps {
  children: React.ReactNode
  /** Render an additional pinned destructive action (e.g. end-call) below the dock. */
  footer?: React.ReactNode
  className?: string
  /** When true, the dock is positioned absolutely at the bottom of its parent. */
  floating?: boolean
}

/**
 * CallControlDock — floating glass strip that holds in-call control buttons.
 * Composes SafeAreaBottom so the iOS home-indicator never overlaps the controls
 * (CLAUDE.md §8). Visually mirrors `.ds-tabbar` styling: 40px radius, blur 40 +
 * saturate 180, midnight-lift shadow.
 */
export function CallControlDock({
  children,
  footer,
  className,
  floating = false,
}: CallControlDockProps) {
  return (
    <SafeAreaBottom
      extra={floating ? 16 : 0}
      className={[
        floating ? 'absolute left-0 right-0 bottom-0' : '',
        'pointer-events-none flex flex-col items-stretch gap-3 px-4',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div
        role="toolbar"
        aria-label="Call controls"
        className="pointer-events-auto"
        // iOS in-call layout: round keys in rows of three, caption under each.
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          justifyItems: 'center',
          rowGap: 16,
          columnGap: 8,
          padding: '6px 10px',
        }}
      >
        {children}
      </div>
      {footer ? <div className="pointer-events-auto">{footer}</div> : null}
    </SafeAreaBottom>
  )
}

// ─── Single dock button ──────────────────────────────────────────────────────

type DockBtnTone = 'neutral' | 'active' | 'danger'

interface CallControlDockButtonProps {
  onClick: () => void
  icon: React.ReactNode
  label: string
  tone?: DockBtnTone
  pressed?: boolean
  disabled?: boolean
  className?: string
  /** Short word under the key ("mute", "keypad"), iOS style. */
  caption?: string
}

const TONE_STYLES: Record<DockBtnTone, { bg: string; border: string; color: string }> = {
  neutral: {
    bg: 'rgb(var(--fg-rgb) / 0.04)',
    border: 'rgb(var(--fg-rgb) / 0.10)',
    color: 'rgb(var(--fg-rgb) / 0.85)',
  },
  active: {
    bg: 'rgb(var(--fg-rgb) / 0.96)',
    border: 'rgb(var(--fg-rgb) / 1)',
    color: 'rgb(var(--bg-rgb))',
  },
  // Hang-up / destructive → blush (phase rule; no legacy reds).
  danger: {
    bg: 'rgb(var(--blush-rgb) / 0.18)',
    border: 'rgb(var(--blush-rgb) / 0.45)',
    color: 'var(--blush-text)',
  },
}

/**
 * Slot for a single icon button inside `<CallControlDock>`. 56×56 circular,
 * MIDNIGHT glass tones, scales 0.94 on :active. Honors `prefers-reduced-motion`.
 */
export function CallControlDockButton({
  onClick,
  icon,
  label,
  tone = 'neutral',
  pressed,
  disabled,
  className,
  caption,
}: CallControlDockButtonProps) {
  const t = TONE_STYLES[tone]
  const key = (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      aria-pressed={pressed}
      title={label}
      className={['call-dock-btn', className].filter(Boolean).join(' ')}
      style={{
        width: 56,
        height: 56,
        borderRadius: '9999px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: t.bg,
        border: `0.5px solid ${t.border}`,
        color: t.color,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        touchAction: 'manipulation',
        transition: 'all 0.2s cubic-bezier(0.2,0.8,0.2,1)',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      {icon}
    </button>
  )
  if (!caption) return key
  return (
    <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      {key}
      <span aria-hidden="true" style={{ fontSize: 12, fontWeight: 500, color: 'var(--midnight-text2)' }}>{caption}</span>
    </span>
  )
}
